/**
 * Standard-UCI parser + UCI->SAN conversion (Workstream B3).
 *
 * The old custom WASM emitted non-standard fields (`baseTurn`, `pvSan`, `bmc`)
 * that the old parser depended on. A stock SF18 build emits ONLY standard UCI:
 *
 *   info depth 20 seldepth 30 multipv 1 score cp 34 nodes ... pv e2e4 e7e5 g1f3
 *   info depth 18 multipv 2 score mate 3 ... pv d1h5 g8f6 h5f7
 *
 * This module:
 *   - parses `multipv N`, `score cp|mate X`, and the `pv <uci moves>` tail;
 *   - converts the UCI pv to SAN client-side via chess.js;
 *   - derives the side-to-move (`baseTurn`) from the FEN, not a custom field.
 *
 * It produces exactly the arguments the existing `onResult` contract expects:
 *   onResult(variationNumber, movesSAN, baseTurn, score, isMate, raw)
 * so all consumers (PlayChess, MovesList, StockfishComponent, Puzzles, Study,
 * ChessOverlayComponent) keep working unchanged.
 */

import {Chess} from 'chess.js';

export interface ParsedInfo {
  /** UCI `multipv N` -> the variation number consumers key on. */
  variationNumber: number;
  /** Principal variation, converted to SAN. */
  movesSAN: string[];
  /** Principal variation in raw UCI move notation. */
  movesUci: string[];
  /** 'w' | 'b', derived from the analyzed FEN's side-to-move field. */
  baseTurn: 'w' | 'b';
  /**
   * Score. Centipawns when !isMate, mate-distance (signed) when isMate.
   * Kept as a string to mirror the old contract (consumers do Number(...)).
   * NOTE: standard UCI scores are from the side-to-move's POV; consumers
   * (StockfishComponent) already flip for baseTurn === 'b', matching the
   * prior behaviour, so we pass the raw side-to-move score through.
   */
  score: string;
  /** True when the score is a mate score. */
  isMate: boolean;
}

/** Derive 'w'/'b' from a FEN's active-color field. Defaults to 'w'. */
/**
 * Strips castling rights that the position cannot actually support, and any
 * en-passant square with no pawn able to use it.
 *
 * Stockfish's FEN parser assumes a castling flag implies a king and rook still
 * on their home squares. Given `KQkq` on a position with no rooks it does not
 * error, reject the position, or play a move — it produces NO OUTPUT AT ALL
 * and hangs. Every consumer then waits forever on an `info … pv` line that is
 * never coming; for the practice opponent that surfaces as a permanent
 * "Computer is thinking…".
 *
 * chess.js is happy to carry such a FEN (it simply never generates a castling
 * move), so a position editor or an imported PGN can hand the app a FEN that
 * works perfectly everywhere except inside the engine. Normalising here — at
 * the single point where a FEN is handed to Stockfish — protects the eval bar,
 * game review, threat mode and the practice opponent alike, rather than
 * relying on every caller to have cleaned its input.
 *
 * Standard chess only: a Chess960 build would need the rook files from the
 * position rather than a1/h1/a8/h8.
 */
export function normalizeFenForEngine(fen: string): string {
  const parts = (fen || '').trim().split(/\s+/);
  if (parts.length < 4) {
    return fen;
  }
  const [placement, turn, castling, enPassant] = parts;

  const ranks = placement.split('/');
  if (ranks.length !== 8) {
    return fen;
  }
  // Expand the placement into a 64-square lookup, a8 first (FEN order).
  const board: Record<string, string> = {};
  ranks.forEach((rank, rankIdx) => {
    let file = 0;
    for (const ch of rank) {
      if (/\d/.test(ch)) {
        file += parseInt(ch, 10);
      } else {
        const square = `${String.fromCharCode(97 + file)}${8 - rankIdx}`;
        board[square] = ch;
        file += 1;
      }
    }
  });

  let nextCastling = '';
  if (castling && castling !== '-') {
    if (castling.includes('K') && board.e1 === 'K' && board.h1 === 'R') {
      nextCastling += 'K';
    }
    if (castling.includes('Q') && board.e1 === 'K' && board.a1 === 'R') {
      nextCastling += 'Q';
    }
    if (castling.includes('k') && board.e8 === 'k' && board.h8 === 'r') {
      nextCastling += 'k';
    }
    if (castling.includes('q') && board.e8 === 'k' && board.a8 === 'r') {
      nextCastling += 'q';
    }
  }

  // An en-passant square is only meaningful with the double-moved pawn still
  // on the board; a stale one is the same class of impossible-state input.
  let nextEnPassant = enPassant || '-';
  if (nextEnPassant !== '-' && /^[a-h][36]$/.test(nextEnPassant)) {
    const file = nextEnPassant[0];
    const rank = nextEnPassant[1];
    const pawnSquare = rank === '3' ? `${file}4` : `${file}5`;
    const expectedPawn = rank === '3' ? 'P' : 'p';
    if (board[pawnSquare] !== expectedPawn) {
      nextEnPassant = '-';
    }
  } else if (nextEnPassant !== '-') {
    nextEnPassant = '-';
  }

  const rest = parts.slice(4);
  return [placement, turn, nextCastling || '-', nextEnPassant, ...rest].join(
    ' ',
  );
}

export function baseTurnFromFen(fen: string): 'w' | 'b' {
  const parts = (fen || '').trim().split(/\s+/);
  return parts[1] === 'b' ? 'b' : 'w';
}

/**
 * Convert a UCI principal variation (e.g. ["e2e4","e7e5","g1f3"]) into SAN
 * (["e4","e5","Nf3"]) by replaying it on a chess.js instance seeded from `fen`.
 * Stops at the first move chess.js rejects (truncated / illegal pv tail) and
 * returns the SAN collected so far — never throws.
 */
export function uciPvToSan(fen: string, uciMoves: string[]): string[] {
  const san: string[] = [];
  let chess: Chess;
  try {
    chess = new Chess(fen);
  } catch {
    return san;
  }
  for (const uci of uciMoves) {
    if (!uci || uci.length < 4) {
      break;
    }
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    try {
      // chess.js@1.0.0-beta.8: move() returns the Move (with .san) or throws
      // on an illegal move. Object form takes UCI-style from/to/promotion.
      const move = chess.move(promotion ? {from, to, promotion} : {from, to}, {
        strict: false,
      } as any);
      if (move && (move as any).san) {
        san.push((move as any).san);
      } else {
        break;
      }
    } catch {
      break;
    }
  }
  return san;
}

/**
 * Parse one standard-UCI `info ... pv ...` line for the given analyzed FEN.
 * Returns null for lines that aren't a multipv info line with a pv.
 */
export function parseInfoLine(raw: string, fen: string): ParsedInfo | null {
  if (!raw || !raw.startsWith('info') || !raw.includes(' pv ')) {
    return null;
  }

  // multipv defaults to 1 when omitted (single-PV searches don't print it).
  const mpvMatch = raw.match(/\bmultipv (\d+)/);
  const variationNumber = mpvMatch ? parseInt(mpvMatch[1], 10) : 1;

  const cpMatch = raw.match(/\bscore cp ([-+]?\d+)/);
  const mateMatch = raw.match(/\bscore mate ([-+]?\d+)/);

  let score = '0';
  let isMate = false;
  if (mateMatch) {
    score = mateMatch[1];
    isMate = true;
  } else if (cpMatch) {
    score = cpMatch[1];
  }

  // The pv is everything after the ` pv ` token (UCI guarantees pv is last).
  const pvIndex = raw.indexOf(' pv ');
  const pvStr = raw.slice(pvIndex + 4).trim();
  const uciMoves = pvStr.length ? pvStr.split(/\s+/) : [];

  const baseTurn = baseTurnFromFen(fen);
  const movesSAN = uciPvToSan(fen, uciMoves);

  return {
    variationNumber,
    movesSAN,
    movesUci: uciMoves,
    baseTurn,
    score,
    isMate,
  };
}
